import {useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useDebounce} from "../hooks/use-debounce";
import {
  useDeletePartner,
  usePartners,
  useTogglePartnerActive,
} from "../hooks/use-partner";
import DataTable from "./tables";

export default function PartnersTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";
  const initialPage = Number(searchParams.get("page") || 1);
  const initialLimit = Number(searchParams.get("limit") || 10);
  const initialIsActive = searchParams.get("isActive");
  const initialIsActiveBool =
    initialIsActive === "true"
      ? true
      : initialIsActive === "false"
      ? false
      : undefined;

  const [globalFilter, setGlobalFilter] = useState(initialSearch);
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [isActive, setIsActive] = useState(initialIsActiveBool);
  const navigate = useNavigate();

  const debouncedFilter = useDebounce(globalFilter, 500);

  useEffect(() => {
    if (debouncedFilter) searchParams.set("search", debouncedFilter);
    else searchParams.delete("search");

    searchParams.set("page", String(page));
    searchParams.set("limit", String(limit));

    if (typeof isActive === "boolean") {
      searchParams.set("isActive", String(isActive));
    } else {
      searchParams.delete("isActive");
    }

    setSearchParams(searchParams, {replace: true});
  }, [debouncedFilter, page, limit, isActive, searchParams, setSearchParams]);

  const {data, isLoading} = usePartners({
    search: debouncedFilter || undefined,
    page,
    limit,
    isActive,
  });

  const rows = data?.data || [];

  const toggleMut = useTogglePartnerActive();
  const deleteMut = useDeletePartner();

  const columns = useMemo(
    () => [
      {
        header: "NAME",
        accessorKey: "partnerName",
        cell: ({getValue}) => <span className="uppercase">{getValue()}</span>,
      },
      {
        header: "LOCATION",
        accessorKey: "partnerLocation",
        cell: ({getValue}) => <span className="capitalize">{getValue()}</span>,
      },
      {
        header: "TARGET TRAINEES",
        accessorKey: "targetTrainees",
        cell: ({getValue}) => {
          const v = getValue();
          return <span className="uppercase">{v ?? "-"}</span>;
        },
      },
      {
        header: "USERS",
        accessorKey: "users",
        cell: ({row}) => {
          const count = Array.isArray(row.original.users)
            ? row.original.users.length
            : 0;
          return <span className="uppercase">{count}</span>;
        },
      },
      {
        header: "STATUS",
        accessorKey: "isActive",
        cell: ({getValue}) => {
          const active = !!getValue();
          return (
            <span
              className={`uppercase px-2 py-1 rounded ${
                active
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {active ? "ACTIVE" : "INACTIVE"}
            </span>
          );
        },
      },
      {
        header: "CREATED",
        accessorKey: "createdAt",
        cell: ({getValue}) => (
          <span className="uppercase">
            {new Date(getValue()).toLocaleDateString()}
          </span>
        ),
      },
      {
        header: "ACTION",
        accessorKey: "action",
        cell: ({row}) => {
          const id = row.original._id;
          const active = row.original.isActive;
          return (
            <div className="flex items-center gap-2">
              <button
                className="uppercase bg-myblue text-white px-3 py-1 rounded-md"
                onClick={() => {
                  navigate(`/partners/${id}`);
                }}
              >
                View
              </button>

              <button
                className="uppercase bg-gray-100 text-gray-900 px-3 py-1 rounded-md"
                onClick={() => toggleMut.mutate({id, nextActive: !active})}
                disabled={toggleMut.isPending}
              >
                {active ? "Deactivate" : "Activate"}
              </button>

              <button
                className="uppercase bg-red-600 text-white px-3 py-1 rounded-md"
                onClick={() => {
                  if (alert("Delete this partner?")) {
                    deleteMut.mutate(id);
                  }
                }}
                disabled={deleteMut.isPending}
              >
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [toggleMut, deleteMut, navigate]
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={globalFilter}
          onChange={(e) => {
            setGlobalFilter(e.target.value);
            setPage(1);
          }}
          placeholder="Search partners..."
          className="w-72 rounded-md border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
        />

        <select
          className="rounded-md border border-gray-300 px-3 py-2"
          value={typeof isActive === "boolean" ? String(isActive) : "all"}
          onChange={(e) => {
            const v = e.target.value;
            setIsActive(v === "all" ? undefined : v === "true");
            setPage(1);
          }}
        >
          <option value="all">All statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        <select
          className="rounded-md border border-gray-300 px-3 py-2"
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(1);
          }}
        >
          <option value={10}>10 / page</option>
          <option value={20}>20 / page</option>
          <option value={50}>50 / page</option>
        </select>
      </div>

      {/* Table */}
      <DataTable
        data={rows || []}
        columns={columns}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        pageSize={limit}
        isLoading={isLoading}
        showSearchBar={false}
      />
    </div>
  );
}
